import { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import type { SortingState } from "@tanstack/react-table";
import { useParams } from "react-router-dom";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";
import Table, { TableControls, TableFooter, TableHeading, type TableAction } from "@/components/Table";
import { toFormat } from "@/lib/dateHelpers";
import { Person, GroupAssignment, RecruitmentLead } from "@/types";
import { GET_PERSON_GROUP_ASSIGNMENTS_QUERY } from "./queries";
import MetricGrid from "@/components/MetricGrid";
import { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import SearchInput from "@/components/SearchInput";
import MultiSelect from "@/components/MultiSelect";
import { useParams as useUrlParams, useSearchWithParams } from "@/hooks/useSearchParams";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import UpsertLeadModal from "./_components/UpsertLeadModal";
import useTableRowMenu from "@/hooks/useTableRowMenu";
import { getGroupHistoryColumns, type GroupStatus, type GroupTableRow } from "./_components/GroupHistoryTableColumns";

type PersonData = { person: Person };

function parseStringArrayParam(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is string => typeof item === "string" && item.length > 0);
}

function formatTableData(list: GroupAssignment[], recruitmentLead?: RecruitmentLead | null): GroupTableRow[] {
  return list.map((assignment: GroupAssignment) => {
    const now = new Date();
    const expiresAt = assignment.expiresAt ? new Date(assignment.expiresAt) : null;
    const isActive = !expiresAt || expiresAt > now;
    const isLeadsGroup = assignment.group.name === "Leads";

    return {
      id: assignment.id,
      groupName: assignment.group.name,
      entryReason: isLeadsGroup ? recruitmentLead?.referralSource || null : null,
      entryReasonZipcode: isLeadsGroup ? recruitmentLead?.zipcode || null : null,
      startDate: assignment.startedAt ?? "",
      expiresAt: assignment.expiresAt ?? null,
      status: isActive ? "Active" : "Expired"
    };
  });
}

function matchesGroupSearch(row: GroupTableRow, search: string) {
  const normalizedSearch = search.trim().toLowerCase();
  if (!normalizedSearch) {
    return true;
  }

  return [
    row.groupName,
    row.entryReason,
    row.entryReasonZipcode,
    toFormat(row.startDate),
    row.expiresAt ? toFormat(row.expiresAt) : null,
    row.status
  ].some((value) => value?.toLowerCase().includes(normalizedSearch));
}

export default function GroupDetails() {
  const { pgaId } = useParams();
  const { params, addSearchParam, deleteSearchParams } = useUrlParams();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "search"
  });
  const selectedGroupValues = useMemo(() => parseStringArrayParam(params.group), [params.group]);
  const selectedStatusValues = useMemo(() => parseStringArrayParam(params.status) as GroupStatus[], [params.status]);
  const [showAddLead, setShowAddlead] = useState(false);
  const [isLeadEdit, setIsLeadEdit] = useState(false);
  const [sortingState, setSortingState] = useState<SortingState>([{ id: "startDate", desc: true }]);

  const { Menu, setMenu } = useTableRowMenu();

  const canManageLeads = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.manageRecruitmentLeads
  });

  const { data, loading, error, refetch } = useQuery<PersonData>(GET_PERSON_GROUP_ASSIGNMENTS_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    skip: !pgaId
  });

  const personData = data?.person;
  const groupAssignments = personData?.groupAssignments ?? [];
  const leadData = personData?.recruitmentLead ?? null;
  const allTableData = useMemo(
    () => formatTableData(personData?.groupAssignments ?? [], personData?.recruitmentLead ?? null),
    [personData?.groupAssignments, personData?.recruitmentLead]
  );

  const isLead = groupAssignments.some((assignment) => assignment.group.name === "Leads");

  const totalHistoricalGroups = groupAssignments.length;
  const totalActiveGroups = groupAssignments.filter((assignment) => {
    const now = new Date();
    const expiresAt = assignment.expiresAt ? new Date(assignment.expiresAt) : null;
    return !expiresAt || expiresAt > now;
  }).length;

  const uniqueGroups = useMemo(() => {
    const groups = [...new Set(allTableData.map((row) => row.groupName))];
    return groups.map((group) => ({ value: group, label: group }));
  }, [allTableData]);

  const uniqueStatuses = useMemo(() => {
    const statuses = [...new Set(allTableData.map((row) => row.status))];
    return statuses.map((status) => ({ value: status, label: status }));
  }, [allTableData]);

  const filteredData = useMemo(() => {
    return allTableData
      .filter((row) => matchesGroupSearch(row, debouncedSearch))
      .filter((row) => selectedGroupValues.length === 0 || selectedGroupValues.includes(row.groupName))
      .filter((row) => selectedStatusValues.length === 0 || selectedStatusValues.includes(row.status));
  }, [allTableData, debouncedSearch, selectedGroupValues, selectedStatusValues]);

  const activeFilterCount = [!!debouncedSearch, selectedGroupValues.length > 0, selectedStatusValues.length > 0].filter(
    Boolean
  ).length;

  const expiresAtSortDesc = sortingState.find((sort) => sort.id === "expiresAt")?.desc ?? false;

  const columns = useMemo(
    () =>
      getGroupHistoryColumns({
        canManageLeads,
        expiresAtSortDesc,
        onEditLead: () => {
          setIsLeadEdit(true);
          setShowAddlead(true);
        },
        setMenu
      }),
    [canManageLeads, expiresAtSortDesc, setMenu]
  );

  function setSelectedGroups(groups: string[]) {
    if (groups.length > 0) {
      addSearchParam("group[]", groups.join(","));
    } else {
      deleteSearchParams("group[]");
    }
  }

  function setSelectedStatuses(statuses: GroupStatus[]) {
    if (statuses.length > 0) {
      addSearchParam("status[]", statuses.join(","));
    } else {
      deleteSearchParams("status[]");
    }
  }

  function clearAllFilters() {
    clearSearch();
    deleteSearchParams(["group[]", "status[]"]);
  }

  if (error) {
    return <ErrorBoundary variant="inline" title="Failed to Load Groups" message={error.message} className="mt-12" />;
  }

  const metrics: MetricCardType<never>[] = [
    {
      label: "Total Historical Groups",
      value: totalHistoricalGroups.toString(),
      iconName: "group"
    },
    {
      label: "Total Active Groups",
      value: totalActiveGroups.toString(),
      iconName: "group"
    }
  ];

  const actions: TableAction[] =
    canManageLeads && !isLead
      ? [
          {
            label: "Add Lead",
            icon: "add" as const,
            onClick: () => {
              setIsLeadEdit(false);
              setShowAddlead(true);
            },
            testId: "create-lead-button"
          }
        ]
      : [];

  return (
    <div data-testid="group-details">
      <Menu />

      <div className="flex flex-col gap-6">
        {metrics.length > 0 && <MetricGrid metrics={metrics} layout="expanded" />}

        {actions.length > 0 && <TableHeading actions={actions} topSpacing="none" />}
        <Table
          data={filteredData}
          loading={loading}
          columns={columns}
          sortingState={sortingState}
          onSortingChange={setSortingState}
          hasActiveFilters={activeFilterCount > 0}
          renderControls={(_table, { onSortClick } = {}) => (
            <TableControls
              useExpandedFilters
              expandedFiltersState={{
                group: selectedGroupValues.length > 0,
                status: selectedStatusValues.length > 0
              }}
              activeFilters={activeFilterCount}
              onClearAllFilters={clearAllFilters}
              onSortClick={onSortClick}
            >
              <SearchInput
                placeholder="Search Groups"
                query={searchValue}
                onChange={setSearchValue}
                onClear={clearSearch}
                data-testid="search-groups-input"
              />
              <div data-name="group">
                <MultiSelect
                  label="Group"
                  selectedValues={selectedGroupValues}
                  className="w-full"
                  positioning="absolute"
                  options={uniqueGroups}
                  onSelect={(value, checked) => {
                    const groups = checked
                      ? [...selectedGroupValues, value]
                      : selectedGroupValues.filter((v) => v !== value);
                    setSelectedGroups(groups);
                  }}
                  onClear={() => {
                    setSelectedGroups([]);
                  }}
                />
              </div>
              <div data-name="status">
                <MultiSelect
                  label="Status"
                  selectedValues={selectedStatusValues}
                  className="w-full"
                  positioning="absolute"
                  options={uniqueStatuses}
                  onSelect={(value, checked) => {
                    const statuses = checked
                      ? [...selectedStatusValues, value as GroupStatus]
                      : selectedStatusValues.filter((v) => v !== value);
                    setSelectedStatuses(statuses);
                  }}
                  onClear={() => {
                    setSelectedStatuses([]);
                  }}
                />
              </div>
            </TableControls>
          )}
          renderNoDataEmptyState={() => (
            <EmptyState
              title="No Group History"
              caption="This person does not have any group assignments."
              iconName="group"
            />
          )}
          renderFilteredEmptyState={() => (
            <EmptyState title="No Results Found" caption="Try a different search or filter." iconName="search" />
          )}
          renderFooter={() => <TableFooter totalRowCount={filteredData.length} />}
          data-testid="group-history-table"
        />
      </div>
      {showAddLead && (
        <UpsertLeadModal
          onClosed={() => {
            setShowAddlead(false);
            setIsLeadEdit(false);
            refetch();
          }}
          variables={{
            id: leadData?.id,
            personId: personData!.id,
            zipcode: leadData?.zipcode,
            referralSource: leadData?.referralSource
          }}
          isUpdate={isLeadEdit}
        />
      )}
    </div>
  );
}
